import React, { useMemo } from 'react'
import InfoCard from '../../../../components/Common/InfoCard'
import { AnswerContainer, AnswersGroup, AnswersWrapper, GaugeContainer, IndentedAnswer, InfoFlex, ResponseBody, TableStyle, TableStyle2, Title } from './style'
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { formatDate } from '../../../../helpers';
import Gauge from '../../../../components/Common/Gauge';
import { ClientQuestionnaireResponseType } from '../../../../types';
import { BUTTON_STYLE } from '../../../../styles/buttons';
import { Button } from 'antd';
import MaterialIcon from '../../../../components/Icons/MaterialIcon';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import FoodIntakeAndDigestionResult from '../../Questionnaires/FoodAndIntakeDigestion/Result';

interface ResponseProps {
  questionnaireResponse: ClientQuestionnaireResponseType
}

export const SocialSupportQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <Title>Your score in social network</Title>
        <GaugeContainer>
          <Gauge
            label="Appraisal Support"
            percent={0.56}
          />
          <Gauge
            label="Belonging Support"
            percent={0.88}
          />
          <Gauge
            label="Tangible Support"
            percent={0.69}
          />
        </GaugeContainer>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const ExpressivityResultResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <GaugeContainer>
          <Gauge
            label="Negative emotionally"
            percent={0.22}
          />
          <Gauge
            label="Positive emotionally"
            percent={0.18}
          />
          <Gauge
            label="Impulse strength"
            percent={0.23}
          />
        </GaugeContainer>
        <ResponseBody>
          <p>
            Emotions help us respond adaptively to environmental challenges and opportunities. Unlike
            other biologically based response tendencies, such as reflexes, emotions only incline us to
            act in certain ways; they do not compel us to do so. This means that we may deny expression
            to some emotional impulses while freely expressing others. Striking individual differences
            in expressivity suggest that people differ in their response tendencies and in how they
            express these impulses as they arise. The Berkeley Expressivity Questionnaire assesses three different facets of
            emotional expressivity:
          </p>
          <p>
            The primary emotions are <strong>anger</strong>, <strong>fear</strong>, <strong>pleasure</strong>,{" "}
            <strong>sadness</strong>, and <strong>disgust</strong>. Emotions can be conceptualized in
            terms of their <strong>functional or adaptive significance</strong> (help us survive).
            Negative emotions such as anger and fear may promote avoidance or defensive behavior,
            whereas the positive emotion of pleasure may facilitate ingestive, exploratory, sexual, or
            novel-seeking behavior.
          </p>
          <p>
            All three facets of emotional expressivity correlate about <strong>0.50</strong> with one
            another. This means that people with strong emotional impulses are more likely to express
            both negative and positive emotions, and those who express negative emotions are also likely
            to express positive emotions. The assessment outcome should mainly be viewed on the
            patterns of the score. For example: A high impulse strength also presupposes a high score on both negative and positive
            emotionality. If negative emotionality remains strongly behind, it means a noticeable effort
            to express negative emotions.
          </p>
          <p>
            The outcome can be a reason for you to ask the client to be guided by behavioral experts
            (coach, psychologist), or to consider the possibility for neurophysiological or epigenic
            disturbances. In this case, ask the client to fill out the following questionnaires:
          </p>
          <ul>
            <li>Behavioral avoidance/inhibition</li>
            <li>Neurotransmitters</li>
          </ul>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const EmotionRegulationQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <GaugeContainer style={{ flexWrap: 'nowrap', gap: 0 }}>
          <Gauge
            label="Cognitive reappraisal"
            percent={0.24}
          />
          <Gauge
            label="Expressive suppression"
            percent={0.19}
          />
        </GaugeContainer>
        <ResponseBody>
          <p>
            A widespread assumption in research and clinical practice is that cognitive reappraisal is a healthy and successful emotion regulation strategy, while expressive suppression is ineffective and has non-favorable consequences (e.g., decreased positive affect, higher physiological arousal).
          </p>
          <p>
            Research has also shown that expressive suppression can have negative social consequences, associated with decreased personal connections and greater difficulty forming relationships. Expressive suppression is widely regarded as a maladaptive emotion regulation strategy. Compared to reassessment, it correlates positively with many mental disorders, is associated with poorer interpersonal outcomes, is negatively related to well-being, and requires the mobilization of a relatively significant number of cognitive resources. However, some researchers argue that context is important when evaluating the adaptability of a strategy, suggesting that suppression may be adaptive in some contexts.
          </p>
          <p>
            The expressive suppression is relatively high. You are advised to ask to client to fill out questionnaire “Conditions of the mother during pregnancy” to determine if epigenetic disturbances might be present. Also ask if the client suffers from addictions.
          </p>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const ConditionsDuringPregnancyOfYourMotherQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <ResponseBody>
          <p>
            <strong>The family conditions during pregnancy were extremely tense.</strong> Discuss the theme “Not feeling safe”. Consider Early Life Stress with epigenic imprinting on the following receptors: Oxytocin, Vasopressin and GABA. Also consider hypothalamus problems and a possible Histamine disbalance.
          </p>
          <p>
            <strong>The health of the mother during pregnancy was not good.</strong> Ask more details during the intake consult! This might have caused Early Life Stress with epigenic imprinting on the following receptors: Oxytocin, Vasopressin and GABA.
          </p>
          <p>
            <strong>The mother used medicines during pregnancy.</strong> Ask more details about the medicines and consult the website: <a href="https://go.drugbank.com"> https://go.drugbank.com</a>. Study the mode of action of the drug to find possible genetic imprinting effects. Consider a treatment to reset the receptors.
          </p>
          <p>
            <strong>The mother did smoke during pregnancy.</strong> This might have caused Early Life Stress with epigenic imprinting on the following receptors: Acetylcholine. Consider a treatment to reset the receptors.
          </p>
          <p>
            <strong>The mother drank alcohol during pregnancy.</strong> This might have caused early life stress with epigenic imprinting on the following receptors: GABA and Glycine. Consider a treatment to reset the receptors.
          </p>
          <p>
            <strong>The mother did no breast feeding, or shorter then 6 months.</strong> Intestinal dysbiosis might be present. Consider carrying out a faeces test or microbiome test.
          </p>
          <p>
            <strong>The client has been sick in the first 2 years.</strong> Discuss the details during the intake consult.
          </p>
          <p>
            <strong>There are other details related to the early stage of life.</strong> Discuss these issues in more details during the intake consult.
          </p>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const BehavioralAvoidanceInhibitionQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <GaugeContainer>
          <Gauge
            label="BIS Drive"
            percent={0.75}
          />
          <Gauge
            label="BAS Drive"
            percent={0.56}
          />
          <Gauge
            label="BAS Fun Seeking"
            percent={0.62}
          />
          <Gauge
            label="BAS Reward Responsiveness"
            percent={0.70}
          />
        </GaugeContainer>
        <ResponseBody>
          <p>
            According to Gray's Theory, the BIS is related to sensitivity to punishment as well as avoidance motivation. It has also been proposed that the BIS is the causal basis of anxiety.
            High activity of the BIS means a heightened sensitivity to no reward, punishment, and novel experience. This higher level of sensitivity to these cues results in a natural avoidance of such environments to prevent negative experiences such as fear, anxiety, frustration, and sadness. People with a highly active BIS have been shown to learn more effectively through use of punishment than by reward. The physiological mechanism behind the BIS is believed to be the septohippocampal system and its monoaminergic afferents from the brainstem.
          </p>
          <p>
            The BIS score is {'>'} 50%. Discuss with the client the option to visit a coach / psychologist to reflect on purposeful living and acting. Also discuss if there are limitations in terms of behavior or neurophysiology.
          </p>
          <p>
            Also take extra notice on answers given in the following questionnaires:
          </p>
          <ul>
            <li>Conditions during pregnancy of your mother</li>
            <li>Emotion Regulation</li>
            <li>Social Support Questionnaire</li>
            <li>Neurotransmitters</li>
          </ul>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const MentalEmotionalFunctioningQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <ResponseBody>
          <p>
            <strong>The self-image is extremely high.</strong> Be alert, this might be compensation behavior.
          </p>
          <p>
            <strong>The client feels uncertain regarding behavior in relationships.</strong> Ask further about the conditions during childhood, and check the answers given in Bis Bas questionnaire, Emotion Regulation questionnaire and Expressivity questionnaire. Try to find out if there is a problem with “connection” and check the conditions during birth. In the case of an oxytocin induced labor and / or a trauma during birth, consider the epigenic imprinting on the following receptors: Oxytocin, Vasopressin and GABA.
          </p>
          <p>
            <strong>The client is or as been shy.</strong> The client feels uncertain regarding other items. Ask further about the conditions during childhood and upbringing and role model of parents, abnormal responsibilities as a child at home, room for emotions, etc. Try to find out if there is a problem with “connection” and check the conditions during birth. In the case of an oxytocin induced labor and / or a trauma during birth, consider the epigenic imprinting on the following receptors: Oxytocin, Vasopressin and GABA.
          </p>
        </ResponseBody>
        <AnswersWrapper>
          <p className="title">Answers</p>
          <AnswersGroup>
            <AnswerContainer>
              <label>What are your most important drivers in life?</label>
              <ul>
                {questionnaireResponse?.answers['1']?.map((answer: string) => (
                  <li>{answer}</li>
                ))}
                <label>Please describe:</label>
                <p>{questionnaireResponse?.answers['1-1']}</p>
              </ul>
            </AnswerContainer>
            <AnswerContainer>
              <label>How do you describe your self image?</label>
              <p className="red">
                <MaterialIcon className="material-icons-sharp" icon="warning" />
                {questionnaireResponse?.answers['2']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>"I was accepted and welcomed as a child by my parents" — how certain do you feel as to how this statement applies to you?</label>
              <p>
                {questionnaireResponse?.answers['3']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>Do you experience uncertainty with your behavior in relationships?</label>
              <p className="red">
                <MaterialIcon className="material-icons-sharp" icon="warning" />
                {questionnaireResponse?.answers['4']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>Do you experience uncertainty with your performance at work?</label>
              <p>
                {questionnaireResponse?.answers['5']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>Do you experience uncertainty with your creativity?</label>
              <p>
                {questionnaireResponse?.answers['6']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>Do you experience uncertainty with your supervisor at work and ability to influence this?</label>
              <p className="red">
                <MaterialIcon className="material-icons-sharp" icon="warning" />
                {questionnaireResponse?.answers['7']}
              </p>
            </AnswerContainer>
            <AnswerContainer>
              <label>Do you have other uncertainties?</label>
              <p>{questionnaireResponse?.answers['8']}</p>
              <IndentedAnswer>
                <label>Score</label>
                <p>{questionnaireResponse?.answers['8-1']}</p>
              </IndentedAnswer>
            </AnswerContainer>
            <AnswerContainer>
              <label>Where you shy as a child?</label>
              <p>{questionnaireResponse?.answers['9']}</p>
              <IndentedAnswer>
                <label>Until which age?</label>
                <p>{questionnaireResponse?.answers['9-1']}</p>
              </IndentedAnswer>
              <IndentedAnswer>
                <label>I am still shy at this moment</label>
                <p>{questionnaireResponse?.answers['9-2']}</p>
                <IndentedAnswer>
                  <label>Rate the level of your shyness:</label>
                  <p>{questionnaireResponse?.answers['9-2-1']}</p>
                </IndentedAnswer>
              </IndentedAnswer>
            </AnswerContainer>
            <AnswerContainer>
              <label>I feel safe / comfortable when I am with other people</label>
              <p>
                {questionnaireResponse?.answers['10']}
              </p>
            </AnswerContainer>
          </AnswersGroup>
        </AnswersWrapper>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const StressIndicatorsQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  const totalsByGroup = useMemo(() => {
    const totals: any = {};

    if (questionnaireResponse?.answers) {
      Object.entries(questionnaireResponse.answers).forEach(([key, value]) => {
        const [group] = key.split('-');
        const numericValue = parseInt(value, 10);

        if (!totals[group]) {
          totals[group] = 0;
        }

        if (!isNaN(numericValue)) {
          totals[group] += numericValue;
        }
      });
    }

    return totals;
  }, [questionnaireResponse]);

  console.log('answersByGroup', totalsByGroup)
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <Title>Your Points</Title>
        <TableStyle>
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Very Low</th>
              <th>Medium</th>
              <th>High</th>
              <th>Very High</th>
              <th>Danger</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Physical Indicators Point Total</td>
              <td className={`very-low ${totalsByGroup['1'] <= 29 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['1'] <= 29 && totalsByGroup['1']}</span>
                  <span>(0 – 29)</span>
                </span>
              </td>
              <td className={`medium ${totalsByGroup['1'] >= 30 && totalsByGroup['1'] <= 37 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['1'] >= 30 && totalsByGroup['1'] <= 37 && totalsByGroup['1']}</span>
                  <span>(30 – 37)</span>
                </span>
              </td>
              <td className={`high ${totalsByGroup['1'] >= 38 && totalsByGroup['1'] <= 47 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['1'] >= 38 && totalsByGroup['1'] <= 47 && totalsByGroup['1']}</span>
                  <span>(38 – 47)</span>
                </span>
              </td>
              <td className={`very-high ${totalsByGroup['1'] >= 48 && totalsByGroup['1'] <= 53 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['1'] >= 48 && totalsByGroup['1'] <= 53 && totalsByGroup['1']}</span>
                  <span>(48 – 53)</span>
                </span>
              </td>
              <td className={`danger ${totalsByGroup['1'] >= 54 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['1'] >= 54 && totalsByGroup['1']}</span>
                  <span>({'>'}54)</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Sleep Indicators Point Total</td>
              <td className={`very-low ${totalsByGroup['2'] <= 7 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['2'] <= 7 && totalsByGroup['2']}</span>
                  <span>(0 – 7)</span>
                </span>
              </td>
              <td className={`medium ${totalsByGroup['2'] >= 8 && totalsByGroup['2'] <= 9 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['2'] >= 8 && totalsByGroup['2'] <= 9 && totalsByGroup['2']}</span>
                  <span>(8 – 9)</span>
                </span>
              </td>
              <td className={`high ${totalsByGroup['2'] >= 10 && totalsByGroup['2'] <= 11 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['2'] >= 10 && totalsByGroup['2'] <= 11 && totalsByGroup['2']}</span>
                  <span>(10 – 11)</span>
                </span>
              </td>
              <td className={`very-high ${totalsByGroup['2'] >= 12 && totalsByGroup['2'] <= 13 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['2'] >= 12 && totalsByGroup['2'] <= 13 && totalsByGroup['2']}</span>
                  <span>(12 – 13)</span>
                </span>
              </td>
              <td className={`danger ${totalsByGroup['2'] > 14 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['2'] >= 14 && totalsByGroup['2']}</span>
                  <span>({'>'}14)</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Behavior Indicators Point Total</td>
              <td className={`very-low ${totalsByGroup['3'] <= 26 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['3'] <= 26 && totalsByGroup['3']}</span>
                  <span>(0 – 26)</span>
                </span>
              </td>
              <td className={`medium ${totalsByGroup['3'] >= 27 && totalsByGroup['3'] <= 35 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['3'] >= 27 && totalsByGroup['3'] <= 35 && totalsByGroup['3']}</span>
                  <span>(27 – 35)</span>
                </span>
              </td>
              <td className={`high ${totalsByGroup['3'] >= 27 && totalsByGroup['3'] <= 35 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['3'] >= 36 && totalsByGroup['3'] <= 44 && totalsByGroup['3']}</span>
                  <span>(36 – 44)</span>
                </span>
              </td>
              <td className={`very-high ${totalsByGroup['3'] >= 45 && totalsByGroup['3'] <= 49 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['3'] >= 45 && totalsByGroup['3'] <= 49 && totalsByGroup['3']}</span>
                  <span>(45 – 49)</span>
                </span>
              </td>
              <td className={`danger ${totalsByGroup['3'] >= 27 && totalsByGroup['3'] <= 35 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['3'] >= 50 && totalsByGroup['3']}</span>
                  <span>({'>'}50)</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Emotional Indicators Point Total</td>
              <td className={`very-low ${totalsByGroup['4'] <= 28 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['4'] <= 28 && totalsByGroup['4']}</span>
                  <span>(0 – 28)</span>
                </span>
              </td>
              <td className={`medium ${totalsByGroup['4'] >= 29 && totalsByGroup['4'] <= 36 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['4'] >= 29 && totalsByGroup['4'] <= 36 && totalsByGroup['4']}</span>
                  <span>(29 – 36)</span>
                </span>
              </td>
              <td className={`high ${totalsByGroup['4'] >= 37 && totalsByGroup['4'] <= 45 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['4'] >= 37 && totalsByGroup['4'] <= 45 && totalsByGroup['4']}</span>
                  <span>(37 – 45)</span>
                </span>
              </td>
              <td className={`very-high ${totalsByGroup['4'] >= 48 && totalsByGroup['4'] <= 54 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['4'] >= 48 && totalsByGroup['4'] <= 54 && totalsByGroup['4']}</span>
                  <span>(48 – 54)</span>
                </span>
              </td>
              <td className={`danger ${totalsByGroup['4'] >= 55 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['4'] >= 55 && totalsByGroup['4']}</span>
                  <span>({'>'}55)</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>Personal Habits</td>
              <td className={`very-low ${totalsByGroup['5'] <= 14 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['5'] <= 14 && totalsByGroup['5']}</span>
                  <span>(0 – 14)</span>
                </span>
              </td>
              <td className={`medium ${totalsByGroup['5'] >= 15 && totalsByGroup['5'] <= 19 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['5'] >= 15 && totalsByGroup['5'] <= 19 && totalsByGroup['5']}</span>
                  <span>(15 – 19)</span>
                </span>
              </td>
              <td className={`high ${totalsByGroup['5'] >= 20 && totalsByGroup['5'] <= 24 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['5'] >= 20 && totalsByGroup['5'] <= 24 && totalsByGroup['5']}</span>
                  <span>(20 – 24)</span>
                </span>
              </td>
              <td className={`very-high ${totalsByGroup['5'] >= 25 && totalsByGroup['5'] <= 29 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['5'] >= 25 && totalsByGroup['5'] <= 29 && totalsByGroup['5']}</span>
                  <span>(25 – 29)</span>
                </span>
              </td>
              <td className={`danger ${totalsByGroup['5'] >= 30 && 'selected'}`}>
                <span>
                  <span>{totalsByGroup['5'] >= 30 && totalsByGroup['5']}</span>
                  <span>({'>'}30)</span>
                </span>
              </td>
            </tr>
          </tbody>
        </TableStyle>
        <ResponseBody>
          <p>
            <strong>Stress caused by physical indicators is present.</strong> Link this information to the physiological regulation disorders.
          </p>
          <p>
            <strong>Stress caused by Sleep Indicators is present.</strong> Advise the client to have a 72-hour HRV measurement performed.
          </p>
          <p>
            <strong>Stress caused by behavior indicators is present.</strong> Consider collaborating with a lifestyle / stress coach during the intervention.
          </p>
          <p>
            <strong>Stress caused by emotional indicators is present.</strong> Consider collaborating with a lifestyle / stress coach during the intervention.
          </p>
          <p>
            <strong>Stress caused by personal habits indicators is present.</strong> Consider collaborating with a lifestyle / stress coach during the intervention.
          </p>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const NeurotransmittersQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  const totalsByGroup = useMemo(() => {
    const totals: any = {};

    if (questionnaireResponse?.answers) {
      Object.entries(questionnaireResponse.answers).forEach(([key, value]) => {
        const [group] = key.split('-');
        const numericValue = parseInt(value, 10);

        if (!totals[group]) {
          totals[group] = 0;
        }

        if (!isNaN(numericValue)) {
          totals[group] += numericValue;
        }
      });
    }

    return totals;
  }, [questionnaireResponse]);

  const series: ApexOptions['series'] = [
    {
      name: 'Dominance',
      data: [totalsByGroup['A1'], totalsByGroup['A2'], totalsByGroup['A3'], totalsByGroup['A4']],
      color: 'rgb(97, 160, 255)'
    },
    {
      name: 'Deficiency',
      data: [totalsByGroup['B1'], totalsByGroup['B2'], totalsByGroup['B3'], totalsByGroup['B4']],
      color: 'rgb(255, 165, 97)',
    },
    {
      name: 'Actual value',
      data: [10, 5, 8, -2],
      color: 'rgb(235, 64, 52)',
    },
  ];

  const options: ApexOptions = {
    chart: {
      id: 'bar',
    },
    legend: {
      position: 'right'
    },
    plotOptions: {
      bar: {
        // distributed: true
      }
    },
    dataLabels: {
      enabled: true,
    },
    yaxis: {
      // labels: {
      //   formatter: function (y: any) {
      //     return y.toFixed(0) + "%";
      //   }
      // }
    },
    xaxis: {
      categories: [
        'Dopamine',
        'Acetylcholine',
        'GABA',
        'Serotonin',
      ],
    }
  };

  console.log('answersByGroup', totalsByGroup)
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <ReactApexChart
          type="bar"
          options={options}
          series={series}
        />
        <TableStyle2>
          <thead>
            <tr>
              <th>Neurotransmitter</th>
              <th>Dominance</th>
              <th>Deficiency</th>
              <th>Actual value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Dopamine</td>
              <td>26</td>
              <td>-14</td>
              <td>12</td>
            </tr>
            <tr>
              <td>Acetylcholine</td>
              <td>26</td>
              <td>-14</td>
              <td>12</td>
            </tr>
            <tr>
              <td>GABA</td>
              <td>26</td>
              <td>-14</td>
              <td>12</td>
            </tr>
            <tr>
              <td>Serotonin</td>
              <td>26</td>
              <td>-14</td>
              <td>12</td>
            </tr>
          </tbody>
        </TableStyle2>
        <ResponseBody>
          <p>
            <strong>Your overall Neurotransmitter functioning (max = 200)</strong>: 44
            <p>
              This is EXTREMELY LOW. Your mental health is out of balance, and it is not possible to achieve your goals. The positive influence of neurotransmitters is not experienced anymore.
            </p>
          </p>
          <p>
            <strong>Your Strength:</strong> You have a predisposition (Natural Dominance) of Serotonin.
          </p>
          <p>
            <strong>Your main Deficiency:</strong> The lowest deficiency is found for GABA.
          </p>
          <p>
            GABA is the No. 1 inhibitory neurotransmitter and serotonin comes in a close second. Serotonin is also the precursor for the production of the sleep hormone melatonin. There is a chance that due to the above there is a poor recovery (during sleep) and that there is therefore a structural dominance of the sympathetic branch of the autonomic nervous system. To rule this out, a multi-day HRV measurement is recommended.
          </p>
          {/* Background Section */}
          <h3>Background:</h3>
          <div>
            <p>
              The 4 neurotransmitters have specific characteristics and stand for:
            </p>
            <p><strong>Dopamine:</strong> Decisiveness, willpower, self-confidence, facts, focus, sharpness, critical, overview, libido.</p>
            <p><strong>Acetylcholine:</strong> Intuitive, keen senses, empathetic, language, art, curious, authentic, out of the box, harmony.</p>
            <p><strong>GABA:</strong> Stability, conviviality, organizing/planning, team player, relationship-oriented, punctual, cautious.</p>
            <p><strong>Serotonin:</strong> Carefree, cheerful, realistic, wants to experience, playful, resourceful, flexible, adventurous, likes to work with hands.</p>
          </div>

          <div>
            <p>Complaints Related to Neurotransmitter Shortages:</p>
            <p><strong>Dopamine:</strong> Indecisive, start/focus problems, flat facial expression, aggressive, addictions, exhaustion.</p>
            <p><strong>Acetylcholine:</strong> Forgetfulness, dyslexia, difficulty in thinking, craving for fatty foods, disrupted automatic actions.</p>
            <p><strong>GABA:</strong> Stress/stimulus sensitive, restless hyper alert, no grip, difficult to stop, cramped, stiff.</p>
            <p><strong>Serotonin:</strong> Trouble sleeping, closed, rigid, worried, migraine, constipation, price sensitive, tense, tunnel vision.</p>
          </div>


          {/* Interpretation Section */}
          <h3>How are the Results Interpreted?</h3>
          <p>
            The neurotransmitter from Natural Dominance (Part A) with the highest score represents the dominant/strong nature. A score of 30 or more is a good sign. If one of the other neurotransmitters is 10-15 points lower than the dominant neurotransmitter, this probably indicates a 'lifelong' relative shortage, which even requires adjustment, even in times of equilibrium.
          </p>
          <p>
            The neurotransmitter with the highest (negative) score in Part B is usually the one that causes symptoms to cause symptoms. The deficient neurotransmitter can be the same as the dominant one, or it can be a different one.
          </p>
          <p>
            The difference between A and B (Your current functioning) is also indicative of complaints, especially with regard to the severity of the complaints. A functioning difference of 25-15 = 10 might look the same as a functioning difference of 15-5 = 10, but the first indicates a deeper/longer causal disturbance than the second.
          </p>

          {/* Suggestions Section */}
          <h3>Suggestions for Intervention:</h3>
          <ol>
            <li>
              Depending on the overall picture (e.g., a low general functioning of the neurotransmitters), and history, there may be grounds for resetting the neurotransmitter receptors.
              <ol type="a">
                <li>
                  Neurotransmitters with Dominance value lower than 30, combined with the neurotransmitter with the largest deficiency, resulting in a low actual value of that neurotransmitter.
                </li>
                <li>Increase the concentration of specific neurotransmitters with supplementation and foods:</li>
              </ol>
            </li>
            <li>Increase the concentration of specific neurotransmitters with supplementation and foods.</li>
            <ol type="a">
              <li>Increase Dopamine levels with: DL-Phenylalanine, Tyrosine, Methionine, Rhodiola, Pyroxidine, B complex, Phosphatidylserine, Ginko Biloba. Dietary modifications can boost dopamine as well. Increase your intake of phenylalanine and tyrosine, eat chicken, cottage cheese, eggs, pork, ricotta cheese, soybean products, turkey, walnuts, wheat germ, granola, oats, duck, and milk and yogurt. To prevent dopamine burnout, practice deep breathing (from abdomen), alternating nostrils, watch non- violent movies and TV shows, weightlifting 3 times per week. Prevent exposure to lead and cadmium. Stop smoking. A cup or two of caffeine in the morning is a very common way to increase dopamine, however this can cause insomnia or tremors if dosage is too high, or it is taken too late in the day.</li>
              <li>Increase Acetylcholine levels with Cytidine Choline, Phosphatidylcholine, Phosphatidylserine, Acetyl-l-carnitine, DHA (Docosahexaenoic acid), Thiamine, Pantothenic Acid, Zinc, Magnesium Vitamin B12, Taurine, Huperzine-A, Ginko Biloba, Korean Ginseng. Dietary medications to increase Acetylcholine are: Beef liver, eggs, soybeans, dairy, poultr and fish.</li>
              <li>Increase GABA levels with: Inositol, Bio-GABA, Glutamic Acid, Melatonin (at night), Thiamine, Niacinamide, Pyridoxine, Valerian root, Passionflower Magnolia bark, skullcap herbs, lemon balm, Jasmine plants, Ashwaganda, Lavendel, theanine, Apigenin, Green tea (EGCG) . Dietary medications to increase GABA are fermented foods rich in probiotics like sauerkraut, kimchi, miso, tempeh, youghurt and kefir. Some foods that contain GABA are fish and shellfish, beans and lentils, brown rice, potatoes, (cherry) tomatoes, seaweed noni fruit and berries. Also, meditation or chronic exercise increases GABA levels.</li>
              <li>Increase Serotonin levels with: Calcium, Fish oil, 5-HTP, Magnesium, Melatonin (at night), Passionflower, Pyridoxine, SAM-e, St. John’s Wort, Tryptophan, Zinc. Some foods the might help serotonin levels are: eggs, cheese, pineapples, tofu, salmon, nuts and seeds, turkey, spinach, soy, milk, cacao, anchivies, sweet potatoes, bananas, plums, tomatoes.</li>
            </ol>
          </ol>
        </ResponseBody>
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};

export const FoodIntakeAndDigestionQuestionnaireResponse: React.FC<ResponseProps> = ({ questionnaireResponse }) => {
  const selectedClient = useSelector((state: RootState) => state.professionals.selectedClient);
  // const totalsByGroup = useMemo(() => {
  //   const totals: any = {};

  //   if (questionnaireResponse?.answers) {
  //     Object.entries(questionnaireResponse.answers).forEach(([key, value]) => {
  //       const [group] = key.split('-');
  //       const numericValue = parseInt(value, 10);

  //       if (!totals[group]) {
  //         totals[group] = 0;
  //       }

  //       if (!isNaN(numericValue)) {
  //         totals[group] += numericValue;
  //       }
  //     });
  //   }

  //   return totals;
  // }, [questionnaireResponse]);
  return (
    <>
      <InfoCard>
        <InfoFlex>
          <p>Client: </p>
          <p>{`${selectedClient?.firstName} ${selectedClient?.familyName} (${selectedClient?.gid})`}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Submitted: </p>
          <p>{formatDate(questionnaireResponse!.updatedAt)}</p>
        </InfoFlex>
        <InfoFlex>
          <p>Assigned: </p>
          <p>{formatDate(questionnaireResponse!.createdAt)}</p>
        </InfoFlex>
      </InfoCard>
      <InfoCard>
        <FoodIntakeAndDigestionResult />
      </InfoCard>
      <Button
        style={{ ...BUTTON_STYLE.primary, width: 'fit-content' }}
        icon={
          <MaterialIcon className="material-icons-round" icon="visibility" />
        }
      >
        Answers
      </Button>
    </>
  )
};
